import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import {
  AdScopeEnum,
  AdTypeEnum,
  StatisticQuery,
  StatisticQueryVariables
} from '@/__generated_graphql__/graphql';
import { ParticipantEnum } from '@/constants/common';
import { GET_STATISTIC } from '@/graphql/queries';

import { useAdsQuery } from './useAdsQuery';

export const useDashboard = (participant: ParticipantEnum) => {
  const isResumes = participant === ParticipantEnum.RESUME;
  const limit = 24;
  const newAdsLimit = 4;

  const adType = useMemo(
    () => (isResumes ? AdTypeEnum.Resume : AdTypeEnum.Vacancy),
    [isResumes]
  );

  const {
    hasMore: hasMoreNewAds,
    ads: newAds,
    loading: newAdsLoading,
    error: newAdsError,
    isLoadingMore: isLoadingMoreNewAds,
    fetchMoreAds: fetchMoreNewAds
  } = useAdsQuery({
    participant,
    variables: { first: newAdsLimit }
  });

  const {
    hasMore,
    ads: featuredAds,
    loading,
    error: featuredError,
    isLoadingMore,
    fetchMoreAds
  } = useAdsQuery({
    participant,
    variables: { scope: AdScopeEnum.Featured, first: limit }
  });

  // Fetch Statistic
  const { data: statisticData, error: statisticError } = useQuery<
    StatisticQuery,
    StatisticQueryVariables
  >(GET_STATISTIC, {
    variables: { adType }
  });

  const statistic = statisticData?.statistic;

  // No Data Condition
  const loadingState = newAdsLoading || loading;
  const isNoData = !newAds.length && !featuredAds.length && !loadingState;

  const isNewCardsView = !!newAds.length || loading;
  const isFeaturedCardsView = !!featuredAds.length || loading;

  // Errors
  const queryError = statisticError || featuredError;

  return {
    hasMore,
    hasMoreNewAds,
    isLoadingMore,
    isLoadingMoreNewAds,
    featuredAds,
    newAds,
    statistic,
    loading,
    newAdsLoading,
    isNoData,
    isFeaturedCardsView,
    isNewCardsView,
    error: queryError,
    newAdsError,
    fetchMoreAds,
    fetchMoreNewAds,
    premiumAdsLength: limit
  };
};
